/**
 * Affiliate Operations — vocabulary and routes.
 *
 * **Every user-visible occurrence of the role name is read from here** (glossary.md §3.3,
 * "Reversibility"), so the label changes in one commit without touching routes, capability
 * slugs or the API. The backend's mirror is `affiliates/constants.py`, and the machine
 * identifiers on both sides — `affiliate_property_officer`, `apo`, `/app/affiliate/*` — are
 * stable forever.
 *
 * The display strings live here rather than in `locales/en.json` for one reason: i18n keys are
 * for *translating* a name, and this is about *changing* it. A single constant is what makes
 * "rename the role" a one-line diff instead of a search through nine JSON files.
 */

export const ROLE_DISPLAY_NAME = 'Affiliate Property Officer'
export const ROLE_DISPLAY_NAME_SHORT = 'Affiliate Officer'
export const ROLE_ABBREVIATION = 'APO'
export const WORKSPACE_NAME = 'Affiliate Operations'
export const DASHBOARD_TITLE = 'Affiliate Operations Dashboard'
export const CODE_LABEL = 'Affiliate Code'
/**
 * An officer an administrator made a supervisor of other officers (2026-10-11). Shown as a tag
 * beside the code in the topbar and on the registers; renamed here, like the role itself.
 */
export const SUPERVISOR_LABEL = 'Supervisor'

/** Machine identifiers. Never varied. */
export const ROLE_CODE = 'affiliate_property_officer'
export const WORKSPACE_KEY = 'affiliate'

/** Route paths, so a link is never a string literal in a component. */
export const ROUTES = {
  overview: '/app/affiliate',
  landlords: '/app/affiliate/landlords',
  landlordNew: '/app/affiliate/landlords/new',
  landlord: (id: number | string) => `/app/affiliate/landlords/${id}`,
  landlordPropertyNew: (id: number | string) => `/app/affiliate/landlords/${id}/properties/new`,
  properties: '/app/affiliate/properties',
  property: (id: number | string) => `/app/affiliate/properties/${id}`,
  /**
   * Editing a property is the onboarding flow again: the permitted fields, then any spaces to
   * add. `step` opens a particular step — `structure` where the page's ask is "add spaces".
   */
  propertyEdit: (
    id: number | string,
    step: 'details' | 'structure' | 'rent' | 'review' = 'details',
  ) => `/app/affiliate/properties/${id}/edit/${step}`,
  /**
   * Phase 07. The portfolio's tenancies, renewals and rent — read-only, except `tenantNew`:
   * adding a tenant to a vacant space where the landlord allowed it (backend phase-07 §3.2).
   */
  tenants: '/app/affiliate/tenants',
  tenant: (id: number | string) => `/app/affiliate/tenants/${id}`,
  tenantNew: (spaceId: number | string) => `/app/affiliate/spaces/${spaceId}/tenants/new`,
  renewals: '/app/affiliate/renewals',
  payments: '/app/affiliate/payments',
  payment: (id: string) => `/app/affiliate/payments/${id}`,
  followUps: '/app/affiliate/follow-ups',
  /** Students asking for spaces near the officer's campuses (2026-10-10). */
  studentDemand: '/app/affiliate/student-demand',
  /** One student's request, by reference -- where an officer takes it on (2026-10-11). */
  studentRequest: (reference: string) =>
    `/app/affiliate/student-demand/requests/${encodeURIComponent(reference)}`,
  /** One student's roommate request, by reference (2026-10-26): handled like a space request. */
  studentRoommate: (reference: string) =>
    `/app/affiliate/student-demand/roommates/${encodeURIComponent(reference)}`,
  /** The escorted group viewings the officer runs, and one of them (2026-10-12). */
  viewings: '/app/affiliate/viewings',
  viewing: (id: string) => `/app/affiliate/viewings/${encodeURIComponent(id)}`,
  activity: '/app/affiliate/activity',
  profile: '/app/affiliate/profile',
  /**
   * A supervisor's register of the other officers (2026-10-11). The one place an affiliate
   * route names another officer: the TARGET's id, never the acting officer's.
   */
  officers: '/app/affiliate/officers',
  officerNew: '/app/affiliate/officers/new',
  officer: (id: string) => `/app/affiliate/officers/${id}`,
} as const

/**
 * The capability slugs this workspace checks, mirroring `affiliates/constants.py`.
 *
 * They are **UX only**: every one is enforced again by a permission class and a selector scope
 * on the server. Hiding a button is not a control (architecture.md §3).
 */
export const CAPABILITIES = {
  selfView: 'affiliate.self.view',
  selfUpdate: 'affiliate.self.update',
  landlordView: 'affiliate.landlord.view',
  landlordCreate: 'affiliate.landlord.create',
  landlordUpdate: 'affiliate.landlord.update',
  landlordInvite: 'affiliate.landlord.invite',
  propertyView: 'affiliate.property.view',
  propertyCreate: 'affiliate.property.create',
  propertyUpdate: 'affiliate.property.update',
  spaceManage: 'affiliate.space.manage',
  spaceUpdate: 'affiliate.space.update',
  duplicateCheck: 'affiliate.duplicate.check',
  noteManage: 'affiliate.note.manage',
  tenantView: 'affiliate.tenant.view',
  rentView: 'affiliate.rent.view',
  commissionView: 'affiliate.commission.view',
  analyticsView: 'affiliate.analytics.view',
  /**
   * A supervisor's (2026-10-11). Not part of the officer set: the session adds them only while
   * the officer is active **and** an administrator made them a supervisor
   * (`affiliates/constants.py::SUPERVISOR_CAPABILITIES`).
   */
  officerView: 'affiliate.officer.view',
  officerManage: 'affiliate.officer.manage',
} as const

/** The officer's lifecycle. Lowercase, matching the wire values. */
export const STATUS = {
  pending: 'pending',
  active: 'active',
  suspended: 'suspended',
  terminated: 'terminated',
} as const

export type AffiliateStatusValue = (typeof STATUS)[keyof typeof STATUS]

/**
 * Human labels for the lifecycle values.
 *
 * The API sends `{value, label}` so the UI never has to know these — but the session summary
 * carries the raw value only, and a pending or terminated officer sees exactly that page
 * because `/affiliate/me/` refuses them. Without this they would read "pending".
 */
export const STATUS_LABELS: Record<string, string> = {
  [STATUS.pending]: 'Pending',
  [STATUS.active]: 'Active',
  [STATUS.suspended]: 'Suspended',
  [STATUS.terminated]: 'Terminated',
}

/** Statuses that may see a dashboard at all. Everything else gets an explainer page. */
export const WORKING_STATUSES: readonly string[] = [STATUS.active, STATUS.suspended]

/**
 * Where a property owner signs up with an officer's code.
 *
 * `route-map.md` fixes the public signup at `/auth/register` with an optional `?ref=<code>`;
 * the route itself lands in Phase 06. The URL is pinned **here**, now, because the profile page
 * prints it into a QR code today (phase-03 §2.1) and a code on a card in a landlord's pocket
 * has to keep working once the route exists. Phase 06 reads the same two constants.
 */
export const SIGNUP_PATH = '/auth/register'
export const SIGNUP_REF_PARAM = 'ref'

export function signupLink(code: string, origin?: string): string {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '')
  return `${base}${SIGNUP_PATH}?${SIGNUP_REF_PARAM}=${encodeURIComponent(code)}`
}

/**
 * Plain-English names for the capability slugs (`affiliates/constants.py`), for the profile
 * page's "what you can do" list. A slug that is not here renders as itself, so a capability a
 * later phase adds is visible before it is named -- never hidden, never mislabelled.
 */
export const CAPABILITY_LABELS: Record<string, string> = {
  'affiliate.self.view': 'View your profile, status and code',
  'affiliate.self.update': 'Update your contact details',
  'affiliate.landlord.create': 'Onboard property owners',
  'affiliate.landlord.view': 'View your landlords',
  'affiliate.landlord.update': 'Update permitted landlord details',
  'affiliate.landlord.invite': 'Resend a landlord invitation',
  'affiliate.property.create': 'Add properties for your landlords',
  'affiliate.property.view': 'View portfolio properties',
  'affiliate.property.update': 'Update permitted property details',
  'affiliate.space.manage': 'Configure sections, blueprints and spaces',
  'affiliate.space.update': 'Update permitted space details',
  'affiliate.media.manage': 'Upload property and space photos',
  'affiliate.tenant.view': 'View tenants and tenancy status',
  'affiliate.rent.view': 'View rent payments and renewals',
  'affiliate.commission.view': 'View your commission and its history',
  'affiliate.analytics.view': 'View portfolio analytics',
  'affiliate.export': 'Export your portfolio data',
  'affiliate.duplicate.check': 'Check whether a landlord or property already exists',
  'affiliate.note.manage': 'Keep follow-up notes on landlords',
  'affiliate.notification.view': 'Read your notifications',
  'affiliate.officer.view': 'See every officer on the register (supervisor)',
  'affiliate.officer.manage':
    'Register, activate, suspend and reinstate other officers (supervisor)',
}

export function capabilityLabel(slug: string): string {
  return CAPABILITY_LABELS[slug] ?? slug
}

/**
 * Locating a building (2026-10-20). The least precise phone position the server accepts — its
 * `campus_lodges.constants.MAX_ACCURACY_M`, mirrored so the page can say so before sending —
 * and when the page stops waiting for a better one.
 */
export const LOCATION_ACCEPTABLE_M = 50
export const LOCATION_GOOD_ENOUGH_M = 15
export const LOCATION_TIMEOUT_MS = 20_000
